import {
  InvalidMoneyError,
  formatMoney,
  money,
  multiplyByRatio,
  parseMoney,
  type Money,
} from "@stb/core/money"
import {
  CENTS_PER_KM_CAP_KM,
  centsPerKmRate,
  compareLogbookVsCentsPerKm,
  type LogbookVsCentsPerKmResult,
} from "@stb/jurisdiction-au"
import { createFileRoute } from "@tanstack/react-router"
import {
  CalendarRange,
  CircleHelp,
  Gauge,
  History,
  ListTree,
  Smartphone,
  Receipt,
  Route as RouteIcon,
  Scale,
} from "lucide-react"
import { useId, useState } from "react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import config from "@/config"
import { useLocale } from "@/core/i18n/locale-context"
import { gateCta } from "@/core/launch"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

import { displayYear, formatCheckedOn } from "../components/home/home-content"
import {
  AnswerBox,
  Breadcrumb,
  CtaBand,
  FaqAccordion,
  RelatedLinks,
  SourceLine,
} from "../components/landing"
import { LogbookTimelineDemo } from "../components/landing/logbook-timeline-demo"
import { HeroBand, RouteSteps, SectionTitle, StatRow } from "../components/landing/page-parts"
import { IconTile } from "../components/landing/product-art"

/** Copy source: docs/design/landing/copy-products.md (`/logbook-method`). */
export const logbookMethodSeo = () =>
  buildHead({
    title: "ATO logbook method: how it works vs cents per km",
    description:
      "How the ATO logbook method works, which costs count and when it beats cents per km. Compare both with your own numbers. Estimate only.",
    path: "/logbook-method",
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Logbook method" }])],
  })

export const Route = createFileRoute("/_brand-layout/logbook-method")({
  head: () => {
    const { meta, links, scripts } = logbookMethodSeo()
    return { meta, links, scripts }
  },
  component: LogbookMethodPage,
})

/**
 * ATO logbook method page. Wording from docs/design/landing (copy.md). `LOGBOOK_CHECKED` is the
 * date the design copy records for the source line.
 */
const LOGBOOK_URL =
  "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-motor-vehicle-expenses/logbook-method"
const LOGBOOK_CHECKED = "2026-10-27"
const FINANCIAL_YEAR = "2026-27"
const RATE = centsPerKmRate(FINANCIAL_YEAR)

const STEPS = [
  {
    title: "Pick 12 weeks",
    body: "A continuous period that looks like a normal stretch of your year.",
  },
  {
    title: "Record each business trip",
    body: "Date, kilometres, odometer readings and a reason. Odometer readings at the start and end of the period too.",
  },
  {
    title: "Work out your business-use share",
    body: "Business kilometres divided by all kilometres in the period.",
  },
  { title: "Add up your car costs", body: "For the full year, with receipts." },
  { title: "Claim the share", body: "Your costs multiplied by the business-use share." },
] as const

type Parsed =
  | { ok: true; result: LogbookVsCentsPerKmResult; km: number; share: string }
  | { ok: false; error: string; field: "km" | "share" | "costs" }

/** Whole km, a business share of 1 to 100 (two decimals at most) and dollars with up to two decimals. */
function compute(kmText: string, shareText: string, costsText: string): Parsed {
  const km = Number(kmText.replaceAll(",", "").trim())
  if (!/^\d[\d,]*$/.test(kmText.trim()) || !Number.isSafeInteger(km)) {
    return {
      ok: false,
      error: "Enter business kilometres as a whole number, such as 4200.",
      field: "km",
    }
  }
  const shareMatch = /^\d{1,3}(\.\d{1,2})?$/.exec(shareText.trim())
  const shareBp = shareMatch ? Math.round(Number(shareText) * 100) : 0
  if (shareBp < 1 || shareBp > 10000) {
    return {
      ok: false,
      error: "Enter a business use share between 1 and 100 percent.",
      field: "share",
    }
  }
  let costs: Money
  try {
    costs = parseMoney(costsText, "AUD")
  } catch (e) {
    if (!(e instanceof InvalidMoneyError)) throw e
    return {
      ok: false,
      error: "Enter yearly car costs in dollars, such as 9800 or 9800.50.",
      field: "costs",
    }
  }
  if (costs.amount < 0) return { ok: false, error: "Car costs cannot be negative.", field: "costs" }
  return {
    ok: true,
    km,
    share: String(shareBp / 100),
    result: compareLogbookVsCentsPerKm({
      financialYear: FINANCIAL_YEAR,
      businessKm: km,
      businessShareBp: shareBp,
      carCosts: costs,
    }),
  }
}

// en-US renders AUD as "A$" (en-AU would drop the prefix), matching the design.
const fmt = (m: Money) => formatMoney(m, "en-US")
/** Display-only rounding to whole dollars; the stored value stays in cents. */
const fmtWhole = (m: Money) =>
  formatMoney(money(multiplyByRatio(m, 1, 100).amount * 100, m.currency), "en-US").replace(
    ".00",
    "",
  )

function Result({
  result,
  km,
  share,
}: {
  result: LogbookVsCentsPerKmResult
  km: number
  share: string
}) {
  const capKm = CENTS_PER_KM_CAP_KM.toLocaleString("en-AU")
  const rate = `${result.rate.cents} c`
  return (
    <div
      role="region"
      aria-label="Comparison result"
      aria-live="polite"
      className="bg-muted grid content-center gap-3 rounded-lg p-5"
    >
      {result.centsPerKm ? (
        <>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground text-sm">
                Cents per km, {displayYear(FINANCIAL_YEAR)} ({rate})
              </dt>
              <dd className="text-2xl font-semibold tabular-nums">{fmt(result.centsPerKm)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">Logbook method</dt>
              <dd className="text-2xl font-semibold tabular-nums">{fmt(result.logbook)}</dd>
            </div>
          </dl>
          <p className="font-medium">
            {result.better === "equal"
              ? "Both methods give the same amount."
              : `${result.better === "logbook" ? "Logbook" : "Cents per km"} is higher by ${fmt(result.difference)}`}
          </p>
          <p className="text-muted-foreground text-sm">
            Estimate only. At {share}% business use and {km.toLocaleString("en-US")} km, the logbook
            method gives more once yearly costs pass about {fmtWhole(result.breakEvenCosts)}. The
            logbook method needs records of your costs. The {capKm} km cap applies to cents per km
            only.
          </p>
        </>
      ) : (
        <>
          <dl>
            <dt className="text-muted-foreground text-sm">Logbook method</dt>
            <dd className="text-2xl font-semibold tabular-nums">{fmt(result.logbook)}</dd>
          </dl>
          <p className="text-muted-foreground text-sm">
            Cents per km counts at most {capKm} km per car. With more business kilometres than that,
            the logbook method may give more. Estimate only; the logbook method needs records of
            your costs.
          </p>
        </>
      )}
    </div>
  )
}

function BreakEvenCalculator() {
  const id = useId()
  const [km, setKm] = useState("4200")
  const [share, setShare] = useState("62")
  const [costs, setCosts] = useState("9800")
  const parsed = compute(km, share, costs)
  const bad = (field: "km" | "share" | "costs") =>
    (!parsed.ok && parsed.field === field) || undefined
  return (
    <div className="border-border bg-card grid gap-5 rounded-xl border p-5 md:grid-cols-2 md:p-6">
      <div className="grid content-start gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-km`}>Business kilometres</Label>
          <Input
            id={`${id}-km`}
            type="text"
            className="h-11 md:h-9"
            inputMode="numeric"
            value={km}
            aria-invalid={bad("km")}
            aria-describedby={bad("km") && `${id}-error`}
            onChange={(e) => setKm(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-share`}>Business use from your logbook (%)</Label>
          <Input
            id={`${id}-share`}
            type="text"
            className="h-11 md:h-9"
            inputMode="decimal"
            value={share}
            aria-invalid={bad("share")}
            aria-describedby={bad("share") && `${id}-error`}
            onChange={(e) => setShare(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-costs`}>
            Car costs for the year in A$ (fuel, registration, insurance, repairs, decline in value)
          </Label>
          <Input
            id={`${id}-costs`}
            type="text"
            className="h-11 md:h-9"
            inputMode="decimal"
            value={costs}
            aria-invalid={bad("costs")}
            aria-describedby={bad("costs") && `${id}-error`}
            onChange={(e) => setCosts(e.target.value)}
          />
        </div>
      </div>
      {parsed.ok ? (
        <Result result={parsed.result} km={parsed.km} share={parsed.share} />
      ) : (
        <p id={`${id}-error`} role="alert" className="text-destructive self-center font-medium">
          {parsed.error}
        </p>
      )}
    </div>
  )
}

const FAQ = [
  {
    question: "Do I need a new logbook every year?",
    answer: "No. A logbook is valid for 5 years, unless your business use changes.",
  },
  {
    question: "What if I forget to record a trip?",
    answer:
      "Add it by hand as soon as you notice. The more complete the logbook, the more reliable the percentage.",
  },
] as const

/** The three numbers the answer box and steps already state: 12 weeks, 5 years, odometer at the start and end. */
const KEY_FACTS = [
  { value: "12 weeks", label: "or more, in a row", icon: CalendarRange, tone: "primary" },
  { value: "5 years", label: "a logbook stays valid, unless your use changes", icon: History },
  { value: "2 readings", label: "odometer at the start and end", icon: Gauge, tone: "success" },
] as const

const TOC = [
  { id: "how-title", label: "How it works" },
  { id: "compare-title", label: "Compare with your numbers" },
  { id: "costs-title", label: "What counts as a cost" },
  { id: "faq-title", label: "Common questions" },
] as const

export function LogbookMethodPage() {
  const trialCta = gateCta({ href: "/login", label: "Start free trial" }, useLocale())
  const checked = formatCheckedOn(LOGBOOK_CHECKED)
  const atoSource = [{ label: "ATO, logbook method", href: LOGBOOK_URL }] as [
    { label: string; href: string },
  ]
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Logbook method" }]} />
      <HeroBand>
        <p className="text-muted-foreground text-sm">Guide · Last reviewed {checked}</p>
        <h1 className="max-w-[24ch] text-3xl font-bold tracking-tight md:text-5xl">
          ATO logbook method: how it works and when it beats cents per km
        </h1>
        <div className="max-w-[70ch]">
          <AnswerBox source={<SourceLine sources={atoSource} checked={checked} />}>
            With the logbook method you keep a logbook for at least 12 continuous weeks that is
            representative of the year, work out your business-use percentage, and claim that share
            of your actual car costs. A logbook stays valid for 5 years unless your use changes.
          </AnswerBox>
        </div>
        <StatRow label="Key facts" stats={KEY_FACTS} />
      </HeroBand>

      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,1fr)_14rem] lg:px-12">
        <div className="min-w-0">
          <section className="grid gap-4 px-4 py-8 md:px-12 lg:px-0" aria-labelledby="how-title">
            <SectionTitle id="how-title" icon={RouteIcon} tone="primary">
              How it works
            </SectionTitle>
            <figure className="grid max-w-[420px] gap-2">
              <LogbookTimelineDemo />
              <figcaption className="text-muted-foreground text-center text-sm">
                Example: a 12-week logbook, filled week by week
              </figcaption>
            </figure>
            <RouteSteps steps={STEPS} />
          </section>

          <section
            className="grid gap-4 px-4 py-8 md:px-12 lg:px-0"
            aria-labelledby="compare-title"
          >
            <SectionTitle id="compare-title" icon={Scale} tone="success">
              Compare with your numbers
            </SectionTitle>
            <p className="text-muted-foreground max-w-[70ch]">
              Cents per km or logbook? The starting numbers are an example. With cents per km you
              can claim at most {CENTS_PER_KM_CAP_KM.toLocaleString("en-AU")} km per car; if you
              drive more, or your costs are high, the logbook method may give a bigger deduction.
            </p>
            <BreakEvenCalculator />
            <SourceLine
              sources={[
                { label: "ATO, logbook method", href: LOGBOOK_URL },
                { label: "ATO, cents per kilometre method", href: RATE.sourceUrl },
              ]}
              financialYear={displayYear(FINANCIAL_YEAR)}
              checked={formatCheckedOn(RATE.checkedOn)}
            />
          </section>

          <section className="grid gap-4 px-4 py-8 md:px-12 lg:px-0" aria-labelledby="costs-title">
            <SectionTitle id="costs-title" icon={Receipt} tone="warning">
              What counts as a cost
            </SectionTitle>
            <p className="max-w-[70ch]">
              Running costs such as fuel, registration, insurance, servicing, repairs and decline in
              value of the car. Keep the receipts and work out the year&apos;s total. The ATO page
              lists what you can include; check it for your car.
            </p>
          </section>

          <section className="grid gap-4 px-4 py-8 md:px-12 lg:px-0" aria-labelledby="faq-title">
            <SectionTitle id="faq-title" icon={CircleHelp} tone="success">
              Common questions
            </SectionTitle>
            <FaqAccordion items={FAQ.map((f) => ({ ...f }))} />
            <SourceLine sources={atoSource} checked={checked} />
          </section>

          <aside
            aria-label="Kestly app"
            className="border-border bg-card mx-4 my-4 flex flex-wrap items-center gap-4 rounded-xl border p-5 md:mx-12 lg:mx-0"
          >
            <IconTile icon={Smartphone} />
            <div className="min-w-0 flex-1">
              <b>12-week logbook mode in the app</b>
              <p className="text-muted-foreground text-sm">
                Week-by-week progress, odometer prompts and a report you can export.
              </p>
            </div>
            <Button asChild>
              <a href={trialCta.href}>{trialCta.label}</a>
            </Button>
          </aside>
        </div>

        <aside className="hidden lg:block">
          <nav
            aria-label="On this page"
            className="border-border bg-card sticky top-6 mt-8 grid gap-2 rounded-xl border p-4 text-sm"
          >
            <b className="flex items-center gap-2">
              <ListTree className="size-4" aria-hidden="true" />
              On this page
            </b>
            {TOC.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm outline-none focus-visible:ring-2"
              >
                {label}
              </a>
            ))}
          </nav>
        </aside>
      </div>

      <section
        role="note"
        aria-label="What Kestly does not do"
        className="bg-accent text-accent-foreground mx-band my-4 grid gap-1 rounded-lg px-5 py-4"
      >
        <h2 className="font-semibold">Limits</h2>
        <p>
          {config.core.projectName} does not give tax advice. These figures are estimates. Check
          your situation with the ATO or a registered tax agent.
        </p>
      </section>

      <RelatedLinks
        links={[
          { label: "Mileage tracker: automatic GPS logbook", href: "/mileage-tracker" },
          {
            label: "ATO logbook requirements: what to record",
            href: "/guides/ato-logbook-requirements",
          },
          { label: "Free ATO logbook template", href: "/ato-logbook-template" },
          { label: "Cents per km rate and calculator", href: "/cents-per-km-calculator" },
        ]}
      />

      <CtaBand
        title="Keep your 12-week logbook in your pocket"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
